import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  useAdminOpsSnapshot,
  ADMIN_OPS_SNAPSHOT_QUERY_KEY,
} from '../../use-admin-ops-snapshot';
import { releaseJobAdmin, terminateJobAdmin } from '../../api';
import { StuckJobsAlertStrip } from './StuckJobsAlertStrip';

interface Props {
  readonly accessToken: string;
}

/**
 * Admin-only stuck-jobs widget mounted inline on the consolidated Activity
 * view. Subscribes to `useAdminOpsSnapshot` (admin-gated endpoint) and owns
 * the release / terminate mutations — both invalidate the ops-snapshot query
 * on success so the row disappears within one refetch rather than waiting on
 * the next polling tick. `busyJobId` blocks double-clicks while a mutation
 * is in flight.
 *
 * Hidden entirely when there are no stuck jobs: the absence of the strip
 * IS the all-clear signal, so admins don't see a "0 alerts" placeholder.
 */
export function AdminStuckJobsSection({ accessToken }: Props) {
  const snapshotQuery = useAdminOpsSnapshot(accessToken);
  const queryClient = useQueryClient();
  const [busyJobId, setBusyJobId] = useState<string | null>(null);

  const releaseMutation = useMutation({
    mutationFn: (jobId: string) => releaseJobAdmin(accessToken, jobId),
    onMutate: (jobId) => setBusyJobId(jobId),
    onSettled: () => setBusyJobId(null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_OPS_SNAPSHOT_QUERY_KEY }),
  });

  const terminateMutation = useMutation({
    mutationFn: (jobId: string) => terminateJobAdmin(accessToken, jobId),
    onMutate: (jobId) => setBusyJobId(jobId),
    onSettled: () => setBusyJobId(null),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ADMIN_OPS_SNAPSHOT_QUERY_KEY }),
  });

  if (!snapshotQuery.data) return null;

  const stuckJobs = snapshotQuery.data.stuckJobs;
  const lastError = releaseMutation.error ?? terminateMutation.error;

  return (
    <>
      <StuckJobsAlertStrip
        stuckJobs={stuckJobs}
        busyJobId={busyJobId}
        onRelease={(jobId) => releaseMutation.mutate(jobId)}
        onTerminate={(jobId) => terminateMutation.mutate(jobId)}
      />
      {lastError && stuckJobs.length > 0 && (
        <div className="admin-action-error" role="status">
          {lastError instanceof Error ? lastError.message : 'Action failed'}
        </div>
      )}
    </>
  );
}
